import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { isPlatformBrowser } from '@angular/common';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Stacked Area Graph Component - 100% Stacked Area Chart
 * @author: PRachethan | 02-08-2025 
 * Modified by: Anil Kumar boddu | 04-08-2025   
 */
@Component({
  selector: 'bi-stacked-area-graph',
  standalone: false,
  templateUrl: './stacked-area-graph.component.html',
  styleUrl: './stacked-area-graph.component.scss'
})
export class StackedAreaGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private stackedAreaGraphReportSubscription: any;
  private isStackedAreaViewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _spinnerService: SpinnerService,
    private _commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.stackedAreaGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Pyramid Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isStackedAreaViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isStackedAreaViewInitialized = true;
    
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('stacked-area-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Stacked Area Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    console.log('Stacked Area Chart - ngOnChanges triggered', this.reportData);
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isStackedAreaViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('stacked-area-chartdiv');
        if (chartContainer) {
          console.log('Stacked Area Chart - Original reportData before transformation:', this.reportData.reportData[0]);
          this.data = this._baseChartService.transformReportData(this.reportData);
          console.log("Stacked Area Graph Data ngOnChanges", this.reportData);
          console.log("Stacked Area Graph Transformed Data", this.data[0]);
          this.updateChart(this.data);
        } else {
          console.log('Stacked Area Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('stacked-area-chartdiv');
            if (retryContainer) {
              console.log('Stacked Area Chart - Original reportData before transformation (retry):', this.reportData.reportData[0]);
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  /**
   * Sanitize field names to be compatible with amCharts
   * @param fieldName - Original field name
   * @returns Sanitized field name
   */
  private sanitizeFieldName(fieldName: string): string {
    // Remove or replace special characters that might cause issues
    return fieldName.replace(/[#@$%^&*()+=<>?:"{}|\\[\]`~]/g, '_');
  }

  /**
   * Create 100% stacked area chart
   * @param data - Chart data
   */
  createChart(data: BarChartData[]) {
    // Prevent multiple chart creations
    if (this.isCreatingChart) {
      console.log('Stacked Area Graph - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    // Check if the chart container exists
    const chartContainer = document.getElementById('stacked-area-chartdiv');
    if (!chartContainer) {
      console.error('Stacked Area Graph - Chart container not found! Make sure element with id "stacked-area-chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Stacked Area Graph - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    // Add a small delay to ensure DOM is ready
    setTimeout(() => {
      try {
        // Double-check DOM element exists
        const retryContainer = document.getElementById('stacked-area-chartdiv');
        if (!retryContainer) {
          console.error('Stacked Area Graph - Chart container still not found after delay');
          this.isCreatingChart = false;
          return;
        }

        console.log('Stacked Area Graph - Creating new 100% stacked area chart...');
        
        // Create root element
        this.root = this._baseChartService.createRoot('stacked-area-chartdiv');
        this._baseChartService.attachExporting(this.root, { 
          filePrefix: 'stacked-area',
          data: data
        });

        // Set themes
        this.root.setThemes([
          am5themes_Animated.new(this.root)
        ]);

        // Create chart
        let chart = this.root.container.children.push(am5xy.XYChart.new(this.root, {
          panX: true,
          panY: true,
          wheelX: "panX",
          wheelY: "zoomX",
          pinchZoomX: true,
          layout: this.root.verticalLayout
        }));

        // Enhanced chart animation
        this._baseChartService.setupChartAnimation(chart, 'slide');

        // Add cursor
        let cursor = chart.set("cursor", am5xy.XYCursor.new(this.root, {
          behavior: "none"
        }));
        cursor.lineY.set("visible", false);

        // Disable any zoom buttons that might be added
        chart.children.each((child: any) => {
          if (child && typeof child.set === 'function') {
            // Disable zoom buttons if they exist
            if (child.get('name') === 'zoom' || child.get('name') === 'zoomButton') {
              child.set('visible', false);
            }
          }
        });

        // Dynamically determine category and value fields from data
        const categoryField = this._baseChartService.getCategoryField(data);
        
        // Get all value fields from the transformed data (which now only contains selected columns)
        const valueFields = this._baseChartService.getValueFields(data);
        
        console.log('Stacked Area Graph - Category field:', categoryField);
        console.log('Stacked Area Graph - Value fields from transformed data:', valueFields);
        console.log('Stacked Area Graph - Sample data item:', data[0]);

        // Create axes
        let xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(this.root, {
          categoryField: categoryField,
          startLocation: 0.5,
          endLocation: 0.5,
          maxZoomFactor: 1,
          renderer: am5xy.AxisRendererX.new(this.root, {
            minorGridEnabled: true,
            minGridDistance: 80
          }),
          tooltip: am5.Tooltip.new(this.root, {})
        }));

        // Set diagonal labels
        xAxis.get("renderer").labels.template.set("rotation", -45);

        // Enhanced axis animation
        this._baseChartService.setupAxisAnimation(xAxis);

        let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(this.root, {
          min: 0,
          max: 100,
          calculateTotals: true,
          numberFormat: "#'%'",
          maxZoomFactor: 1,
          renderer: am5xy.AxisRendererY.new(this.root, {})
        }));

        // Enhanced axis animation for y axis
        this._baseChartService.setupAxisAnimation(yAxis);

        // Set data
        xAxis.data.setAll(data);

        // Create series for each value field using 100% stacked area visualization
        valueFields.forEach((valueField, index) => {
          let series = chart.series.push(am5xy.LineSeries.new(this.root, {
            name: valueField,
            stacked: true,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: valueField,
            categoryXField: categoryField,
            valueYShow: "valueYTotalPercent",
            legendValueText: "{valueY}",
            tooltip: am5.Tooltip.new(this.root, {
              pointerOrientation: "horizontal",
              labelText: "[bold]{name}[/]\n{categoryX}: {valueYTotalPercent.formatNumber('#.0')}% ({valueY.formatNumber('#,##0')})"
            })
          }));

          // Enhanced series animation
          this._baseChartService.setupSeriesAnimation(series, 'elastic');

          // Configure fills for area effect
          series.fills.template.setAll({
            fillOpacity: 0.5,
            visible: true
          });

          // Set enhanced tooltip with value and label display
          const tooltip = this._baseChartService.createStandardTooltip(this.root, 'stacked-area', {
            showPercentage: true,
            showValue: true,
            showCategory: true,
            showSeries: true
          });
          //series.set('tooltip', tooltip);

          // Set data
          series.data.setAll(data);
          
          // Animate series appearance
          series.appear(1000);
        });

        // Add scrollbar on top of the chart
        /* chart.set("scrollbarX", am5.Scrollbar.new(this.root, {
          orientation: "horizontal"
        })); */

        // Disable any zoom controls that might be added by amCharts
        setTimeout(() => {
          // Disable zoom buttons in chart children
          chart.children.each((child: any) => {
            if (child && typeof child.set === 'function') {
              // Check for zoom-related elements
              const childName = child.get('name') || '';
              const childClass = child.constructor.name || '';
              
              if (childName.toLowerCase().includes('zoom') || 
                  childClass.toLowerCase().includes('zoom') ||
                  childName.toLowerCase().includes('button') && childName.toLowerCase().includes('zoom')) {
                child.set('visible', false);
                console.log('Disabled zoom control:', childName, childClass);
              }
            }
          });

          // Also check for any zoom controls in the root container
          this.root.container.children.each((child: any) => {
            if (child && typeof child.set === 'function') {
              const childName = child.get('name') || '';
              const childClass = child.constructor.name || '';
              
              if (childName.toLowerCase().includes('zoom') || 
                  childClass.toLowerCase().includes('zoom')) {
                child.set('visible', false);
                console.log('Disabled zoom control in root:', childName, childClass);
              }
            }
          });

          // Also check DOM for any zoom controls and hide them
          const chartContainer = document.getElementById('stacked-area-chartdiv');
          if (chartContainer) {
            const zoomElements = chartContainer.querySelectorAll('[class*="zoom"], [id*="zoom"], [title*="zoom"]');
            zoomElements.forEach((element: any) => {
              if (element.style) {
                element.style.display = 'none';
                console.log('Hidden zoom element in DOM:', element);
              }
            });
          }
        }, 100);

        // Add legend
        let legend = chart.children.push(am5.Legend.new(this.root, {
          centerX: am5.p50,
          x: am5.p50
        }));

        // Enhanced legend animation
        this._baseChartService.setupLegendAnimation(legend);

        // Configure legend
        legend.set('maxHeight', 40);

        // Configure legend markers
        let markerTemplate = legend.markers.template;
        markerTemplate.set('width', 12);
        markerTemplate.set('height', 12);

        // Set legend data
        legend.data.setAll(chart.series.values);

        // Make stuff animate on load
        chart.appear(1000, 100);

        console.log('Stacked Area Graph - 100% stacked area chart created successfully');
        this.isCreatingChart = false;

      } catch (error) {
        console.error('Error creating 100% stacked area chart:', error);
        this.isCreatingChart = false;
      }
    }, 100);
  }

  /**
   * Update the chart with new data
   * @param data - New data to display
   */
  private updateChart(data: BarChartData[]) {
    console.log('Stacked Area Chart - updateChart called with data:', data);
    console.log('Stacked Area Chart - updateChart data length:', data.length);
    console.log('Stacked Area Chart - updateChart first item:', data[0]);
    console.log('Stacked Area Chart - updateChart first item keys:', Object.keys(data[0] || {}));
    
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Stacked Area Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    console.log('Stacked Area Chart - Updating chart...');

    // Update the stored data
    this.data = data;

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('stacked-area-chartdiv');
    if (!chartContainer) {
      console.log('Stacked Area Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Dispose existing chart first
    if (this.root) {
      console.log('Stacked Area Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    // Hide spinner if it's still showing
    this._spinnerService.hide();
    this._baseChartService.disposeChart(this.root);
    this.stackedAreaGraphReportSubscription.unsubscribe();
  }
}
